import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { DialogStatusComponent } from '../dialog/dialog-status/dialog-status.component';
import { UserLogin } from '../user/login/login.component';
import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';


@Injectable({
  providedIn: 'root'
})
export class AppService {

  public isUpdateUserAccount: boolean;
  public isUpdateServiceAccount: boolean;
  public isUpdateInstrument: boolean;
  public isUpdateHoliday: boolean;
  public currentUser: LoggedUserAccount;
  public needTemplate: boolean = true;
  public addOnUrl: string = '';

  public isLoadingSpinner: boolean = false;

  public hasUnsavedChanges: boolean = false;
  public miniSidebarOpen: boolean = true;

  // temporary user details for use in resend twofactor
  public userTemp: UserLogin = null;

  public passwordExpiresIn: number = 0;

  public supportedLanguages: string[] = ['en', 'es'];

  private localeMap: Record<string, any> = {
    en: localeEn,
    es: localeEs,
  };


  constructor(private router: Router, private http: HttpClient, @Inject('BASE_URL') private baseUrl: string, private translate: TranslateService, private dialog: MatDialog) {
    this.CheckAuth();
  }
  
  /**
 * Checks if the user is authenticated.
 * If not already loaded, it will fetch the current user from the API.
 * Also sets the correct Angular locale data based on the user's browser language.
 */
  CheckAuth(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      // If we haven't already set the currentUser, or the user is not authenticated
      if (this.currentUser === undefined || this.currentUser.isAuthenticated === false) {

        // Call the backend API to check token validity and get user info
        this.http.get<LoggedUserAccount>(this.baseUrl + 'api/auth/authenticateUserToken').subscribe(result => {

          // Get the browser's current language, e.g. 'en-US' or 'es-ES'
          const currentLanguage = this.getLocalLanguage()?.split('-')[0]; // Normalize to 'en' or 'es'

          // Register the corresponding Angular locale if supported
          if (currentLanguage && this.localeMap[currentLanguage]) {
            registerLocaleData(this.localeMap[currentLanguage]);
          }

          // Save the user and resolve the promise with authentication status
          this.currentUser = result;
          resolve(this.currentUser.isAuthenticated);

        }, error => {
          // If the request fails, reject the promise
          reject(error);
        });

      } else {
        // Already authenticated — resolve immediately
        resolve(this.currentUser.isAuthenticated);
      }
    });
  }

  /**
   * Setting the translation file according to the browser's language and current component
   * @param path translation file path
   */
  SetTranslation(path: string) {
    let currentLanguage = this.translate.getBrowserLang();
    if (this.supportedLanguages.includes(currentLanguage) === false || currentLanguage === undefined) {
      currentLanguage = 'en';
    }
    this.translate.use(path + '_' + currentLanguage);
  }

  /**
   * Gets the remaining time
   * @param marketId Selected market id
   * @param timeColumn Selected time column
   */
  GetRemainingTime(marketId: string, timeColumn: string): Promise<RemainingTime> {
    let remainingTime: RemainingTime = {
      time: '0:00:00',
      timeColumn: ''
    };
    return new Promise((resolveTime, reject) => {
      this.http.get<RemainingTime>(this.baseUrl + 'api/common/remaining/' + marketId + '/' + timeColumn).subscribe(result => {
        remainingTime = result;
        resolveTime(remainingTime);
      }, error => {
        resolveTime(remainingTime);
        console.error(error);
      });
    });
  }

  /**
   * Checking the token status and display status message
   * @param inValidToken Bool indicates the token status
   * @param title Status title
   * @param message Status message
   * @param ignoreTranslate Ignore the translation or not
   */
  SaveStatus(inValidToken: boolean, title: string, message: string, ignoreTranslate?: boolean) {

    if (!ignoreTranslate) {
      title = this.translate.instant(title);
      message = this.translate.instant(message);
    }

    if (inValidToken === false) {
      this.dialog.open(DialogStatusComponent, {
        disableClose: true,
        data: {
          title: title,
          message: message,
          innerHTML: true
        },
        position: { top: '5%' }
      });
    } else {
      this.dialog.open(DialogStatusComponent, {
        disableClose: true,
        data: {
          title: title,
          message: message,
          innerHTML: true
        },
        position: { top: '5%' }
      }).afterClosed().subscribe(res => {
        this.currentUser.isAuthenticated = false;
        this.router.navigate(['login']);
      });
    }
  }

  OnStepperClick(value: any, direction: number, step: number): number {
    let number = Number(String(value).replace(',', '.'));

    if (direction > 0) {
      number = number + step;
    } else {
      number = number - step;
    }

    return number;
  }

  OnStepperChange(value: any, type: string): any {
    let returnVal;

    switch (type) {
      default:
        returnVal = String(value).replace(',', '.');
        if (isNaN(Number(returnVal))) {
          returnVal = String(returnVal).replace(/[^\d,.-]/g, '');
        }
        break;
    }

    return returnVal;
  }

  /** Setting the culture info as a cookie */
  SetCultureInfo() {
    this.http.get(this.baseUrl + 'api/common/setCulture/' + this.getLocalLanguage()).subscribe();
  }


  /**
   * Sets a cookie in the browser
   * @param name cookie name
   * @param value cookie value
   * @param expirydays no of expiry days
   */
  setCookie(name: string, value: string, expirydays: number) {
    const d: Date = new Date();
    d.setTime(d.getTime() + expirydays * 24 * 60 * 60 * 1000);
    const expires: string = 'expires=' + d.toUTCString();
    document.cookie = name + '=' + value + '; ' + expires + '; SameSite=None; secure=true;';
  }

  /**
   * Gets s cookie value from the browser
   * @param name name of the cookie
   */
  getCookie(name: string): string {
    let cookie = '';
    const ca: Array<string> = document.cookie.split(';');
    const caLen: number = ca.length;
    const cookieName = name;
    let c: string;

    for (let i: number = 0; i < caLen; i += 1) {
      c = ca[i].replace(/^\s+/g, '');
      if (c.indexOf(cookieName) === 0) {
        cookie = c.substring(cookieName.length, c.length).replace('=', '');
      }
    }

    return cookie;
  }

  /** Passes the boolean string and returns the converted boolean */
  getBoolean(value): boolean {

    switch (value) {
      case true:
      case 'true':
      case 1:
      case '1':
      case 'on':
      case 'yes':
        return true;
      default:
        return false;
    }
  }

  // getLocale(): Promise<string> {
  //   return new Promise((resolveLocal, reject) => {
  //     let currentLanguage = navigator.language.slice(0, navigator.language.indexOf('-'));
  //     console.log(currentLanguage);
  //     if (this.supportedLanguages.includes(currentLanguage) === false || currentLanguage === undefined) {
  //       currentLanguage = 'en-US';
  //       resolveLocal(currentLanguage);
  //     } else {
  //       resolveLocal(import('@angular/common/locales/' + navigator.language + '.js').then(module => {
  //         registerLocaleData(module.default);
  //         return navigator.language;
  //       }));
  //     }
  //   });
  // }

  /** Gets the browser local language */
  getLocalLanguage(): string {
    // console.log(this.translate.getBrowserLang());
    // let currentLanguage = navigator.language.slice(0, navigator.language.indexOf('-'));
    //   if (currentLanguage === undefined) {
    //     currentLanguage = 'en-US';
    //   } else {
    //     currentLanguage = navigator.language;
    //   }
    let currentLanguage = this.translate.getBrowserLang();

    if (this.supportedLanguages.includes(currentLanguage) === false || currentLanguage === undefined) {
      currentLanguage = 'en';
    }
    return currentLanguage;
  }

  /** Checks the cultureinfo cookie */
  checkCultureCookie() {
    const cookie = this.getCookie('_cultureInfo');
    if (cookie !== this.getLocalLanguage()) {
      this.SetCultureInfo();
    }
  }

  /**
   * Checks the sidebar cookie in the browser to remember the user's sidebar preference
   * @param from From sidebar or from the initiation
   */
  checkSidebarCookie(from: number) {
    let hasCorrectCookie: Boolean = true;
    let newCookie = String(this.miniSidebarOpen);
    const cookie = this.getCookie('sidebar_preference');
    if (cookie === '') {
      hasCorrectCookie = false;
    } else if (cookie !== String(this.miniSidebarOpen)) {
      hasCorrectCookie = false;
      this.setCookie('sidebar_preference', '', -1);
    }

    if (!hasCorrectCookie) {
      if (from === 0) {
        newCookie = cookie;
      }
      this.setCookie('sidebar_preference', newCookie, 365);
    }
  }

  /** Shows the spinner overlay */
  showSpinner() {
    this.isLoadingSpinner = true;
  }

  /** Hides the spinner overlay */
  hideSpinner() {
    setTimeout(() => {
      this.isLoadingSpinner = false;
    }, 500);
  }
}

/** An interface to access response status */
export interface GRSSResponse {
  // code 1: succeeded
  // code 2: failed
  // code 3: server error
  // code 4: invalid token
  statusCode: number;
  statusMessage: string;
}

/** An interface to access logged in user's details */
interface LoggedUserAccount {
  responseStatus: GRSSResponse;
  userInitials: string;
  isAuthenticated: boolean;
  isTestingEnabled: boolean;
  isReadOnly: boolean;
  userId: string;
  userRole: string;
  userRoleID: string;
  accountId: string;
  siteTitle: string;
  defaultUrl: string;
  hasGeneralMarketsToManage: boolean;
  userContributeMarkets: UserGeneralMarket[];
  userAssessMarkets: UserGeneralMarket[];
  userRoles: ValueIdPair[];
}

/** An interface to access user's general markets (Contribute and Assess)  */
interface UserGeneralMarket {
  marketId: string;
  marketCode: string;
  isTransactional: boolean;
}

/** An interface to access value id pairs used in several places */
export interface ValueIdPair {
  id: string;
  value: string;
}

/** An interface to access remaining time details */
interface RemainingTime {
  timeColumn: string;
  time: string;
}

/** An enumerator for user roles */
export enum GRSSUserRoles {
  NotSelected = 'NotSelected',
  Admin = 'Admin',
  Compliance = 'Compliance',
  Operator = 'Operator',
  Assessor = 'Assessor',
  Contributor = 'Contributor',
  Staff = 'Staff',
  BankCompliance = 'BankCompliance'
}

export interface InitSettings{
  azureSPAId: string;
  azureAPIId: string;
  azureTenantId: string;
}
